/**
 * "Log a contact" — the drawer's action zone.
 *
 * The channel picker is `BasicButtonGroup`, the segmented control the settings modal uses for
 * notification frequency (Instant / Hourly / Daily). Three separate `Button`s never fit the 400px
 * panel — the labels ellipsized — and the house already has a control for "pick one of a short
 * list", so this uses that rather than inventing a segmented control.
 *
 * Pinned to the bottom of the left panel by the panel's `justify-between`. Its own card border
 * separates it from the identity rows above, so there is no divider.
 */
import { ChangeEvent, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { GSIcon } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { BasicTextArea } from 'src/components/shared/form/BasicTextArea'
import {
  BasicButtonGroup,
  BasicButtonGroupOption,
} from 'src/components/shared/form/BasicButtonGroup'
import { showToast } from 'src/utils/toast'
import { ContactChannel } from './types'

interface Props {
  contractorName: string
  /** Rejects when the write fails; the caller has already surfaced the error. */
  onLogged(channel: ContactChannel, note: string): Promise<void>
}

/** Labels only, one word each — the group has no icon slot and the segments are ~120px wide. */
const CHANNELS: BasicButtonGroupOption[] = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'text', label: 'Text' },
]

const LogContactFormComponent = ({ contractorName, onLogged }: Props) => {
  const [channel, setChannel] = useState<ContactChannel>('note')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  /** A call or a text is an event on its own; a note is nothing but its text. */
  const isNoteRequired = channel === 'note'

  const handleChangeChannel = useCallback((value: string | number) => {
    setChannel(value as ContactChannel)
    setError('')
  }, [])

  const handleChangeNote = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value)
    setError('')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) {
      return
    }

    const trimmed = note.trim()

    if (isNoteRequired && !trimmed) {
      setError('Note is required')

      return
    }

    setIsSubmitting(true)

    try {
      await onLogged(channel, trimmed)
      setNote('')
      setError('')
      showToast(`Contact logged for ${contractorName}`, { variant: 'success' })
    } catch {
      /** The caller toasts the failure; keep the note so it is not retyped. */
    } finally {
      setIsSubmitting(false)
    }
  }, [channel, isSubmitting, isNoteRequired, note, contractorName, onLogged])

  return (
    <div css={tw`flex flex-col p-4`}>
      {/* Same wrapper the submission list uses in `ContractorStepDetailsCardContent`:
          `rounded-lg`, 1px `SHADES_GREY_100`. */}
      <div
        css={[
          tw`flex flex-col rounded-lg border border-solid p-4`,
          { borderColor: colors.SHADES_GREY_100 },
        ]}
      >
        <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-2`}>
          Log a contact
        </Typography>

        {/* Wrapped, not `css={tw`mb-4`}` on the components themselves: the emotion pragma turns a
            `css` prop on a component into `className`, and neither `BasicButtonGroup` nor
            `BasicTextArea` forwards that — the margin was silently dropped. */}
        <div css={tw`mb-4`}>
          <BasicButtonGroup value={channel} options={CHANNELS} onChange={handleChangeChannel} />
        </div>

        <div css={tw`mb-4`}>
          <BasicTextArea
            name="note"
            label={isNoteRequired ? 'Note' : 'Note (optional)'}
            placeholder={isNoteRequired ? 'What did you note?' : 'Add an optional note...'}
            value={note}
            error={Boolean(error)}
            helperText={error}
            fullWidth
            // MUI's default outline is `rgba(0, 0, 0, 0.23)`, darker than anything in the palette.
            // `SHADES_GREY_100` matches the card border around this whole block. Focus and error
            // keep their own colours.
            sx={{
              '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                { borderColor: colors.SHADES_GREY_100 },
            }}
            onChange={handleChangeNote}
          />
        </div>

        {/* Same shape as the action-zone button in `ContractorActions`: light, small, full width,
            36px. Contractor details only ever uses `grey` and `light` here — `primary` is the
            vivid `#7F00FF` that `00-tokens.md` § Colour priority bars from large fills. */}
        <Button
          color="light"
          size="small"
          fullWidth
          disabled={isSubmitting}
          css={tw`h-[36px]`}
          data-testid="priority-queue-log-contact-btn"
          onClick={handleSubmit}
        >
          Log contact
        </Button>

        <div css={tw`flex flex-row items-center gap-2 mt-2`}>
          <span css={tw`shrink-0`}>
            <GSIcon
              iconName="gs_circle_info_3"
              width={20}
              height={20}
              color={colors.SHADES_GREY_400}
            />
          </span>
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            Records outreach you have already made. GigSafe does not place the call or send the
            text.
          </Typography>
        </div>
      </div>
    </div>
  )
}

export const LogContactForm = memo(LogContactFormComponent)
